import { forwardRef } from 'react'
import { Box, LinearProgress, Typography } from '@mui/material'
import KeyboardDoubleArrowUpIcon from '@mui/icons-material/KeyboardDoubleArrowUp'
import HelpOutlineIcon from '@mui/icons-material/HelpOutlined'
import SecDimAlert from '../../shared/SecDimAlert/SecDimDialogAlert'
import Tooltip from '../../shared/Tooltip/Tooltip'
import DrSecDimCard from './DrSecDimCard'
import DrSecDimQuestionnaire from './DrSecDimQuestionnaire'
import DrSecDimSummary from './DrSecDimSummary'
import { compactPanel } from './drSecDimConfig'
import * as Styled from './DrSecDimStyles'

export type PanelMode = 'view' | 'summary' | 'preferences'

interface DrSecDimPanelProps {
	isOpen: boolean
	viewState: MentorViewState
	mode: PanelMode
	mentor: MentorSnapshot | null
	customisation: UserCustomisation | null
	// First-run welcome, e.g. "Hey Alex, let's build a personalised learning
	// pathway for you." Shown as the heading when entering setup from the empty
	// state.
	greeting: string
	// True while the Celery worker is generating a learning path.
	isGenerating?: boolean
	// Live progress message from the WebSocket, e.g.
	// "Retrieving your current learning path."
	generatingMessage?: string | null
	// Determinate progress simulated from the worker's current generation phase.
	generationProgress?: number
	onOpenPreferences: () => void
	onCompletePreferences: (customisation: UserCustomisation) => void
	onReturnToView: () => void
	onRegenerate: () => void
	onClose: () => void
	selectedNodeIds: Set<string>
	onToggleNode: (id: string) => void
	selectedCount: number
	isRegenerateBusy: boolean
	canRegenerate: boolean
	onRegenerateControlClick: () => void
	onRestartTutorial: () => void
}

const DrSecDimPanel = forwardRef<HTMLDivElement, DrSecDimPanelProps>(
	(
		{
			isOpen,
			viewState,
			mode,
			mentor,
			customisation,
			greeting,
			isGenerating,
			generatingMessage,
			generationProgress = 0,
			onOpenPreferences,
			onCompletePreferences,
			onReturnToView,
			onRegenerate,
			onClose,
			selectedNodeIds,
			onToggleNode,
			selectedCount,
			isRegenerateBusy,
			canRegenerate,
			onRegenerateControlClick,
			onRestartTutorial,
		},
		ref,
	) => {
		const renderContent = () => {
			// Generation in progress: show the SecDim logo, a determinate
			// progress bar, and
			// the live WebSocket progress message.
			if (isGenerating && mode === 'view') {
				return (
					<Styled.PanelCenter>
						<Box
							sx={{
								width: 'min(100%, 820px)',
								display: 'flex',
								flexDirection: 'column',
								alignItems: 'center',
								gap: 1,
								px: 3,
								pb: 6,
							}}
						>
							<Typography variant='h4' sx={{ textAlign: 'center' }}>
								Creating Your Learning Path
							</Typography>
							<Typography
								variant='body1'
								color='text.secondary'
								sx={{ textAlign: 'center', mb: 5 }}
							>
								Free to close this panel and come back later.
							</Typography>
							<Box
								sx={{
									width: '100%',
									display: 'flex',
									flexDirection: 'column',
									gap: 0.75,
								}}
							>
								<Typography
									variant='body1'
									color='text.secondary'
									sx={{ textAlign: 'left' }}
								>
									{generatingMessage
										? generatingMessage
										: 'Generating your learning path… This may take a few minutes.'}
								</Typography>
								<LinearProgress
									variant='determinate'
									value={generationProgress}
									aria-label={
										generatingMessage ??
										'Generating your learning path. This may take a few minutes.'
									}
								/>
							</Box>
						</Box>
					</Styled.PanelCenter>
				)
			}

			if (mode === 'preferences') {
				return (
					<DrSecDimQuestionnaire
						initialCustomisation={customisation}
						onComplete={onCompletePreferences}
						onCancel={onReturnToView}
					/>
				)
			}

			if (mode === 'summary') {
				return (
					<DrSecDimSummary
						customisation={customisation}
						onEdit={onOpenPreferences}
						onRegenerate={onRegenerate}
						onCancel={onReturnToView}
						canRegenerate={canRegenerate}
					/>
				)
			}

			switch (viewState) {
				case 'loading':
					return (
						<Styled.PanelCenter>
							<Typography
								variant='body1'
								color='text.secondary'
								sx={{ textAlign: 'center', px: 3 }}
							>
								Loading…
							</Typography>
						</Styled.PanelCenter>
					)
				case 'error':
					return (
						<Styled.PanelCenter>
							<SecDimAlert
								severity='error'
								alertMessage='Dr. SecDim is unavailable right now. Please try again later.'
							/>
						</Styled.PanelCenter>
					)
				case 'empty':
					// No pathway yet: greet the learner and drop them straight into
					// step 1 instead of a separate "no pathway" screen. Cancelling here
					// closes the panel since there is nothing to return to.
					return (
						<DrSecDimQuestionnaire
							initialCustomisation={null}
							onComplete={onCompletePreferences}
							onCancel={onClose}
							greeting={greeting}
						/>
					)
				case 'ready':
				default:
					return mentor ? (
						<DrSecDimCard
							mentor={mentor}
							onClose={onClose}
							selectedNodeIds={selectedNodeIds}
							onToggleNode={onToggleNode}
							selectedCount={selectedCount}
							isRegenerateBusy={isRegenerateBusy}
							canRegenerate={canRegenerate}
							onRegenerateControlClick={onRegenerateControlClick}
							onRestartTutorial={onRestartTutorial}
						/>
					) : null
			}
		}

		// The "ready" state (learning path already exists) and the generation
		// progress screen both render shorter content than the setup wizard, so
		// the panel can shrink to fit them instead of reserving the wizard's
		// full height.
		const isCompact = compactPanel(mode, viewState, Boolean(isGenerating), mentor)

		return (
			<Styled.Panel
				ref={ref}
				id='dr-secdim-panel'
				data-tour='dr-secdim-panel'
				role='dialog'
				aria-label='Dr. SecDim'
				tabIndex={-1}
				className={isOpen ? 'state-visible' : ''}
				compact={isCompact}
			>
				{!(viewState === 'ready' && mentor) && (
					<Styled.PanelTopBar>
						<Box sx={{ position: 'absolute', right: 0, top: 0 }}>
							<Tooltip title='Replay tutorial'>
								<Styled.PanelCloseButton
									type='button'
									aria-label='Replay Dr. SecDim tutorial'
									onClick={onRestartTutorial}
								>
									<HelpOutlineIcon fontSize='inherit' />
								</Styled.PanelCloseButton>
							</Tooltip>
						</Box>
						<Styled.PanelCloseButton
							type='button'
							data-tour='dr-secdim-close'
							aria-label='Close Dr. SecDim'
							onClick={onClose}
						>
							<KeyboardDoubleArrowUpIcon fontSize='inherit' />
						</Styled.PanelCloseButton>
					</Styled.PanelTopBar>
				)}
				{renderContent()}
			</Styled.Panel>
		)
	},
)

DrSecDimPanel.displayName = 'DrSecDimPanel'

export default DrSecDimPanel
